import React, { useEffect, useRef, useState } from 'react';
import { Title, useDataProvider } from 'react-admin';

import { DataGrid } from '@mui/x-data-grid';
import { Grid, Typography, Box } from '@mui/material';
import RenderControls from '../../../components/ra-list/RenderControls';
import { dayjs } from '../../../components/ex_dayjs';

const RAReport = () => {
    const [state, setState] = useState({
        loaded: false,
        data: {}
    });

    //Current date in PST
    const pstNow = dayjs().tz('America/Los_Angeles');
    const [defaultValues] = useState({
        order_date: [pstNow, pstNow]
    });

    // Compute the totals
    const computeTotals = (arr: any) => {
        const totals = arr.reduce((acc: any, item: any) => {
            acc.unProcessed += item.unProcessed || 0;
            acc.total += item.total || 0;
            acc.profit += item.profit || 0;
            acc.lost += item.lost || 0;
            acc.penalties += item.penalties || 0;
            acc.totalProfit += item.totalProfit || 0;
            return acc;
        }, { accountId: '999', accountName: 'Total', unProcessed: 0, total: 0, profit: 0, lost: 0, penalties: 0, totalProfit: 0 });
        return totals;
    };

    const dataProvider = useDataProvider();
    const handleSearch = (params: any) => {
        setState({ ...state, loaded: false });

        let fDate = params.order_date_ge ?? pstNow.format('YYYY-MM-DD') + 'T00:00:00Z';
        let tDate = params.order_date_le ?? pstNow.format('YYYY-MM-DD') + 'T23:59:59Z';
        let query = `Index?from=${fDate}&to=${tDate}` + '&utcOffset=' + pstNow.utcOffset() * 60;
        dataProvider
            .fetchData('ResaleOrdersReport', query)
            .then((response: any) => {
                let resale = response.data.accounts;
                setState({
                    ...state, loaded: true, data: {
                        resale: [...resale, computeTotals(resale)],
                    }
                });
            });
    };
    const refFilter = useRef(null);
    useEffect(() => {
        handleSearch(defaultValues);
    }, []);

    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2// round the currency value to 2 decimal places
    });

    const usdPrice = {
        type: 'number',
        // eslint-disable-next-line eqeqeq -- loose equality preserved intentionally: raw grid values may arrive as numeric strings and must still match 0
        valueFormatter: (value) => value != 0 ? currencyFormatter.format(value) : 0
    };

    const MyDataGrid = (props: any) => {
        const { id, title, sortArr } = props;
        const [sortModel, setSortModel] = useState(sortArr);

        const handleSortModelChange = (newModel: any) => {
            setSortModel(newModel);
        };

        return (
            <Box sx={{
                '& .MuiDataGrid-root.hide-pagination .MuiDataGrid-footerContainer': {
                    display: 'none'
                },
                '& .MuiDataGrid-cell.text-red, .MuiDataGrid-columnHeader.text-red': {
                    color: 'red',
                },
                width: '100%', minHeight: 150
            }}>
                <Typography display="inline" variant="h6" gutterBottom flex="1" fontWeight="bold">{title}</Typography>
                {props.subtitle && <Typography display="inline" variant="subtitle2"> {props.subtitle}</Typography>}
                <DataGrid
                    {...props}
                    // eslint-disable-next-line eqeqeq -- loose equality preserved intentionally: GridRowId may be a string, and the "Total" row's id must still match 999
                    getRowClassName={(_, _index) => _.id == 999 ? 'row-bold' : ''}
                    density="compact"
                    autoHeight
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    // Add a unique id prop to each DataGrid component
                    id={id}
                    disableColumnMenu={true}
                    className="hide-pagination"
                    hideFooterPagination={true}
                />
            </Box>
        );
    };

    return (
        <Box>
            <Title title={'Resale entry report'}></Title>
            <Grid container columnSpacing={2}>
                <Grid item size={12}>
                    <RenderControls
                        refFilter={refFilter}
                        controls={[
                            { component: 'daterange', name: 'order_date', label: 'PST: Order date (from)', xs: 3 }
                        ]}
                        defaultValues={defaultValues}
                        onSearch={handleSearch}
                        disabled={!state.loaded}
                    />

                </Grid>
                <Grid item size={6.5}>
                    {
                        /* CUSTOM ORDERS */
                        <MyDataGrid
                            id="resale"
                            subtitle="Resale orders (do not contain the 'spec' tag)"
                            rows={state?.data?.resale || []}
                            columns={[
                                // eslint-disable-next-line eqeqeq -- loose equality preserved intentionally: raw grid values may arrive as numeric strings and must still match 999
                                { field: 'accountId', headerName: 'Acct #', type: 'number', valueFormatter: (value) => value != 999 ? value : '' },
                                {
                                    field: 'accountName', headerName: 'Account Name', flex: 1
                                },
                                {
                                    field: 'unProcessed', headerName: 'Unprocessed', type: 'number', width: 120
                                },
                                { field: 'total', headerName: 'Total Orders', type: 'number', width: 120 },
                                { field: 'profit', headerName: 'Profit', type: 'number', ...usdPrice },
                                { field: 'lost', headerName: 'Lost', type: 'number', ...usdPrice },
                                { field: 'penalties', headerClassName: 'text-red', headerName: 'Penalties', cellClassName: 'text-red', type: 'number', ...usdPrice },
                                { field: 'totalProfit', headerName: 'Total Profit', type: 'number', ...usdPrice }
                            ]}
                            sortArr={[{ field: 'accountId', sort: 'asc' }]}
                            getRowId={(r) => `${r.accountId}-${r.accountName}`}
                        />
                    }
                </Grid>
            </Grid>
        </Box>
    );
};

const RAPage = {
    name: 'ResaleOrdersReport',
    list: RAReport
};
export default RAPage;
